import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import { ENDPOINTS } from "../Seguridad/Utils/Endpoints";
import { modulosObjectoNormalize } from "../Seguridad/Objetos/ModulosObjetosModel";
// import { Models } from "./../../utils/Models"; // DEPRECATED

import { useLogos } from "../../context/LogoProvider";

export const SigmaTopbar = ({
  activeTop,
  onChangeTab,
  onToggleSidebar,
  sidebarOpen,
  // 🔹 nuevo: idUsuario desde el shell
  idUsuario,
  // 🔹 nuevo: callback opcional para ir al home
  onGoHome,
}) => {
  const navigate = useNavigate();
  const { getLogoUrl, refreshResources } = useLogos(); // Consumir contexto
  // Ref para evitar loops infinitos de recarga
  const hasRefreshedRef = React.useRef(false);
  // Efecto para resetear el ref si cambia el usuario (logout->login con otro user)
  useEffect(() => {
    hasRefreshedRef.current = false;
  }, [idUsuario]);
  // Efecto para asegurar que los recursos se actualicen AL MONTARSE el componente con un usuario válido
  useEffect(() => {
    // Si hay usuario y NO hemos refrescado aún en esta instancia
    if (idUsuario && !hasRefreshedRef.current) {
      // console.log("[SigmaTopbar] Usuario detectado, refrescando recursos visuales...");
      refreshResources();
      hasRefreshedRef.current = true;
    }
  }, [idUsuario, refreshResources]);

  const [mods, setMods] = useState([]);
  const [loading, setLoading] = useState(false);

  // Cargar módulos para pestañas superiores
  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      try {
        // Si no hay usuario en sesión, no cargamos módulos
        if (!idUsuario) {
          console.warn(
            "[SigmaTopbar] No hay idUsuario en sesión. No se cargan módulos topbar."
          );
          setMods([]);
          return;
        }

        setLoading(true);
        const res = await api.get(ENDPOINTS.modulos + "/permitidos", {
          params: { IdUsuario: idUsuario },
          signal: controller.signal,
        });

        const arr = (res?.data || []).map(modulosObjectoNormalize).filter(Boolean);
        setMods(arr);

        if (arr.length > 0) {
          const hasActive = arr.some(
            (m) => String(m.idModulo) === String(activeTop)
          );

          // Solo cambiamos la pestaña activa si encontramos un idModulo válido
          if (!hasActive && typeof onChangeTab === "function") {
            const firstId = arr[0]?.idModulo;
            if (firstId !== undefined && firstId !== null) {
              onChangeTab(String(firstId));
            }
          }
        }
      } catch (err) {
        if (err.name !== "CanceledError")
          console.error("[SigmaTopbar] load:", err);
      } finally {
        setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [idUsuario, onChangeTab, activeTop]);

  const tabs = useMemo(() => mods, [mods]);

  // Navegar al panel principal (usa onGoHome si viene; si no, /menu)
  const handleBrandClick = () => {
    if (typeof onGoHome === "function") {
      onGoHome();
    } else {
      navigate("/menu");
    }
  };

  // Logout → ir a la portada pública
  const handleLogout = async () => {
    try {
      // 1. Intentar llamar al backend para eliminar cookies
      await api.post(ENDPOINTS.usuarioLogout);
    } catch (err) {
      console.warn("[Logout] Error de servidor (esperado tras cambio de seguridad):", err);
    } finally {
      // 2. Limpiar sesión localmente SIEMPRE (incluso si falla el servidor)
      import('../../utils/SessionUser').then(m => {
        m.clearSessionUser();
        if (refreshResources) refreshResources();
        navigate("/");
      });
    }
  };

  return (
    <header className="sk-topbar" role="banner">
      {/* Marca / logo (regresa al panel principal) */}
      <div
        className="sk-topbar__brand"
        aria-label="SigmaKey"
        role="button"
        tabIndex={0}
        onClick={handleBrandClick}
        onKeyDown={(e) =>
          (e.key === "Enter" || e.key === " ") && handleBrandClick()
        }
      >
        <div className="sk-brand__logos">
          {getLogoUrl("Logo Principal") && (
            <img
              src={getLogoUrl("Logo Principal")}
              alt="TuCoachMexico"
              className="sk-brand__logo-top"
              title="Tipo: Logo Principal"
            />
          )}
        </div>
      </div>

      {/* Pestañas de módulos */}
      <nav className="sk-top-tabs">
        {loading && tabs.length === 0 && (
          <button className="sk-top-tab sk-top-tab--loading" disabled>
            Cargando…
          </button>
        )}
        {tabs.map((tab, index) => {
          const key =
            tab.idModulo !== undefined && tab.idModulo !== null
              ? `mod-${tab.idModulo}`
              : `mod-idx-${index}`;

          const tabId =
            tab.idModulo !== undefined && tab.idModulo !== null
              ? String(tab.idModulo)
              : String(index);

          const isActive = String(activeTop) === tabId;
          const label = tab.clave ?? tab.nombre ?? `Módulo ${tabId}`;
          return (
            <button
              key={key}
              type="button"
              className={"sk-top-tab" + (isActive ? " sk-top-tab--active" : "")}
              aria-current={isActive ? "page" : undefined}
              onClick={() => onChangeTab && onChangeTab(tabId)}
            >
              {label}
            </button>
          );
        })}
      </nav>

      {/* Acciones derecha: toggle sidebar (móvil) + logout */}
      <div className="sk-topbar__actions">
        <button
          type="button"
          className="sk-hamburger sk-only-mobile"
          onClick={onToggleSidebar}
          aria-label={sidebarOpen ? "Cerrar menú lateral" : "Abrir menú lateral"}
        >
          <span />
          <span />
          <span />
        </button>

        {/* Botón Logout con candado y texto */}
        <button
          type="button"
          className="sk-logout-btn"
          onClick={handleLogout}
          title="Cerrar sesión"
          aria-label="Cerrar sesión"
        >
          <span className="sk-logout-icon">
            <i className="fas fa-lock"></i>
          </span>
          <span className="sk-logout-text">Logout</span>
        </button>
      </div>
    </header>
  );
};
